import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { useRouter } from "next/router";

import { ModalProps as MuiModalProps } from "@mui/material";

import { useLadyIdContext } from "@/context/LadyId.context";

import { GlobalModal, GlobalModalPropTypes } from "../GlobalModal";
import {
  ModalComponentProps,
  ModalComponentType,
  ModalConstantKeys,
} from "../constants";

type ModalConfig<K extends ModalConstantKeys = ModalConstantKeys> = {
  ModalComponent: ModalComponentType<K>;
  modalProps: ModalComponentProps<K>;
  globalModalProps: GlobalModalPropTypes & Partial<MuiModalProps>;
  id: string;
};

type GlobalModalContextType = {
  addModal: <K extends ModalConstantKeys>(
    newmodal: ModalComponentType<K>,
    modalProps: ModalComponentProps<K>,
    globalModalProps: GlobalModalPropTypes & Partial<MuiModalProps>,
  ) => string;
  updateModalProps: <K extends ModalConstantKeys>(
    id: string,
    newModalProps: Partial<ModalComponentProps<K>>,
  ) => void;
  removeOne: () => void;
  removeAll: () => void;
  openedModals: ModalConfig[];
};

export const GlobalModalContext = createContext<null | Omit<
  ReturnType<typeof useCreateGlobalModalContextValue>,
  "globalProps"
>>(null);

const useCreateGlobalModalContextValue = () => {
  const [openedModals, setOpenedModals] = useState<
    GlobalModalContextType["openedModals"]
  >([]);

  const addModal: GlobalModalContextType["addModal"] = (
    ModalComponent,
    modalProps,
    globalModalProps,
  ) => {
    const id = `${Date.now()}-${Math.random().toString(36).substring(2)}`;
    setOpenedModals((state) => [
      ...state,
      { ModalComponent, modalProps, globalModalProps, id },
    ]);
    return id;
  };

  const updateModalProps = <K extends ModalConstantKeys>(
    id: string,
    newProps: Partial<ModalComponentProps<K>>,
  ) => {
    setOpenedModals((state) =>
      state.map((modal) =>
        modal.id === id
          ? { ...modal, modalProps: { ...modal.modalProps, ...newProps } }
          : modal,
      ),
    );
  };
  const removeOne = () => {
    setOpenedModals((state) => {
      return state.slice(0, -1);
    });
  };

  const removeAll = () => {
    setOpenedModals([]);
  };

  const router = useRouter();

  useEffect(() => {
    router.beforePopState(({ as }) => {
      if (as !== router.asPath) {
        removeAll();
      }
      return true;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [router]);

  const { resetLadyId } = useLadyIdContext();

  useEffect(() => {
    if (openedModals.length === 0) {
      resetLadyId();
    }
  }, [openedModals, resetLadyId]);

  return {
    openedModals,
    addModal,
    updateModalProps,
    removeOne,
    removeAll,
  };
};

export const GlobalModalContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const contextValue = useCreateGlobalModalContextValue();

  return (
    <GlobalModalContext.Provider value={contextValue}>
      {children}
      <GlobalModal />
    </GlobalModalContext.Provider>
  );
};

export const useGlobalModalContext = () => {
  const globalModalContext = useContext(GlobalModalContext);
  if (!globalModalContext) throw new Error("Use app context within provider!");

  return globalModalContext;
};
